import type {
  APIComponentAttributes,
  APIFieldAttributes,
  APIReferenceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import type { ReferenceFilterContext } from '@ardoq/filter-helpers';
import {
  globalFilters as Filters,
  type ComponentFilterData,
  type ReferenceFilterData,
} from 'collections/filters';

type ScopeDataFilterChecks = {
  isComponentIncludedInContextByFilter: (
    component: APIComponentAttributes,
    ctx?: ReferenceFilterContext
  ) => boolean;
  isReferenceIncludedInContextByFilter: (
    reference: APIReferenceAttributes,
    ctx?: ReferenceFilterContext
  ) => boolean;
};

const fieldsOf = (
  scopeData: EnhancedScopeData,
  modelId: ArdoqId,
  typeId: ArdoqId | number
): APIFieldAttributes[] =>
  Object.values(
    scopeData.fieldsByModelIdAndTypeId[`${modelId}-${typeId}`] ?? {}
  );

const toComponentFilterData = (
  scopeData: EnhancedScopeData,
  component: APIComponentAttributes,
  ctx: ReferenceFilterContext | undefined
): ComponentFilterData => ({
  attributes: component,
  fields: fieldsOf(scopeData, component.model, component.typeId),
  typeName: ctx?.componentTypeByComponentId[component._id]?.name ?? '',
  incomingRefCount:
    scopeData.graphData.targetMap.get(component._id)?.length ?? 0,
  outgoingRefCount:
    scopeData.graphData.sourceMap.get(component._id)?.length ?? 0,
});

const toReferenceFilterData = (
  scopeData: EnhancedScopeData,
  reference: APIReferenceAttributes,
  ctx: ReferenceFilterContext | undefined
): ReferenceFilterData => ({
  attributes: reference,
  fields: fieldsOf(scopeData, reference.model, reference.type),
  typeName: ctx?.referenceTypeByReferenceId[reference._id]?.name ?? '',
});

/**
 * The perspective filter checks workspace mode applies, evaluated against
 * `scopeData` instead of the loaded workspaces.
 */
export const getScopeDataFilterChecks = (
  scopeData: EnhancedScopeData
): ScopeDataFilterChecks => ({
  isComponentIncludedInContextByFilter: (component, ctx) =>
    Filters.isComponentIncludedInContextByFilter(
      null,
      toComponentFilterData(scopeData, component, ctx),
      scopeData.graphData
    ),
  isReferenceIncludedInContextByFilter: (reference, ctx) => {
    const source = scopeData.componentsById[reference.source];
    const target = scopeData.componentsById[reference.target];
    if (!source || !target) return true;
    return Filters.isReferenceIncludedInContextByFilter(
      null,
      toReferenceFilterData(scopeData, reference, ctx),
      toComponentFilterData(scopeData, source, ctx),
      toComponentFilterData(scopeData, target, ctx),
      scopeData.graphData
    );
  },
});
